import * as React from "react";

// Sentinel to distinguish "no provider" from any valid context value
const NO_PROVIDER = Symbol("NO_PROVIDER");

interface ContainerProviderProps<State = void> {
  initialState?: State;
  children: React.ReactNode;
}

export interface Container<Value, State = void> {
  Provider: React.ComponentType<ContainerProviderProps<State>>;
  useContainer: () => Value;
}

/**
 * Create a context container from a hook function that returns
 * an object that 'is' the state value.
 *
 * This can be seeded with initial state -- and is pretty clever
 * with type inference for your passed in hook function.
 */
export const createContainer = <Value, State = void>(
  useHook: (initialState?: State) => Value
): Container<Value, State> => {
  // Use sentinel as default to properly detect missing providers
  // (Previously used {} which could become frozen and cause "Cannot add property" errors)
  const Context = React.createContext<Value | typeof NO_PROVIDER>(NO_PROVIDER);

  const Provider = (props: ContainerProviderProps<State>) => {
    // Hook must return a valid value - no fallback to empty object
    const value = useHook(props.initialState);
    return <Context.Provider value={value}>{props.children}</Context.Provider>;
  };

  /**
   * Use the container in a component down the react tree.
   *
   * If called outside a Provider, falls back to calling the hook directly
   * for backwards compatibility, but warns in development.
   */
  const useContainer = (): Value => {
    const contextValue = React.useContext(Context);

    // If provider exists, use context value
    if (contextValue !== NO_PROVIDER) {
      return contextValue;
    }

    // Fallback: call hook directly (backwards compat, but warn in dev)
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "useContainer called outside Provider - this may cause issues. " +
          "Ensure component is wrapped in the appropriate Container."
      );
    }
    return useHook();
  };

  return { Provider, useContainer };
};
